import { useState } from "react";
import "./JobApplicationModal.css";
import { createCareer } from "../../api/CareerApi";

const JobApplicationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    linkedin_portfolio_link: "",
    resume: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const maxSize = 5 * 1024 * 1024; // 5 MB

    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a PDF or Word document.");
      return;
    }

    if (file.size > maxSize) {
      setError("Resume size must be less than 5 MB.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      resume: file,
    }));

    setError("");
  };

  const isFormFilled =
    formData.name.trim() !== "" &&
    formData.phone.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.linkedin_portfolio_link.trim() !== "" &&
    formData.resume !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormFilled || loading) return;

    try {
      setLoading(true);
      setError("");

      const data = new FormData();

      data.append("name", formData.name.trim());
      data.append("phone_number", formData.phone.trim());
      data.append("email", formData.email.trim());
      data.append(
        "linkedin_portfolio_link",
        formData.linkedin_portfolio_link.trim()
      );

      if (formData.resume) {
        data.append("resume", formData.resume);
      }

      await createCareer(data);

      setSuccess(true);

      setFormData({
        name: "",
        phone: "",
        email: "",
        linkedin_portfolio_link: "",
        resume: null,
      });
    } catch (err) {
      console.error("Career application error:", err);

      const detail = err?.response?.data?.detail;

      if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item.msg)
            .join(", ")
        );
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      linkedin_portfolio_link: "",
      resume: null,
    });

    setError("");
    setSuccess(false);
    setLoading(false);

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="job-application-modal__overlay">
      <div className="job-application-modal">
        <div className="job-application-modal__header">
          <h2>Job Application Form</h2>

          <button
            type="button"
            className="job-application-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {success ? (
          <div className="job-application-modal__success">
            <h3>Application Submitted!</h3>

            <p>
              Thank you for applying to Xyncely.
              We’ll get back to you soon.
            </p>

            <button
              type="button"
              className="job-application-modal__submit job-application-modal__submit--active"
              onClick={handleClose}
            >
              Done <span>→</span>
            </button>
          </div>
        ) : (
          <form
            className="job-application-modal__form"
            onSubmit={handleSubmit}
          >
            {/* NAME */}
            <div className="job-application-modal__field">
              <label htmlFor="job-application-name">
                Full Name <span>*</span>
              </label>

              <input
                id="job-application-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Alex Rivera"
                required
              />
            </div>

            {/* PHONE */}
            <div className="job-application-modal__field">
              <label htmlFor="job-application-phone">
                Phone Number <span>*</span>
              </label>

              <input
                id="job-application-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g. +91 98765 43210"
                required
              />
            </div>

            {/* EMAIL */}
            <div className="job-application-modal__field">
              <label htmlFor="job-application-email">
                Email Address <span>*</span>
              </label>

              <input
                id="job-application-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
              />
            </div>

            {/* LINKEDIN / PORTFOLIO */}
            <div className="job-application-modal__field">
              <label htmlFor="job-application-link">
                LinkedIn or Portfolio Link <span>*</span>
              </label>

              <input
                id="job-application-link"
                type="url"
                name="linkedin_portfolio_link"
                value={formData.linkedin_portfolio_link}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/username"
                required
              />
            </div>

            {/* RESUME */}
           <div className="job-application-modal__field">
  <label htmlFor="job-application-resume">
    Upload Resume <span>*</span>
  </label>

  <label
    htmlFor="job-application-resume"
    className="job-application-modal__resume-upload"
  >
    <span className="job-application-modal__upload-icon">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 16V4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M8 8L12 4L16 8"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 14V18C5 19.1 5.9 20 7 20H17C18.1 20 19 19.1 19 18V14"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </span>

    <span className="job-application-modal__upload-title">
      {formData.resume
        ? formData.resume.name
        : "Upload your resume"}
    </span>

    <span className="job-application-modal__upload-info">
      PDF or Word • Max 5 MB
    </span>
  </label>

  <input
    id="job-application-resume"
    type="file"
    accept=".pdf,.doc,.docx"
    onChange={handleResumeChange}
    hidden
  />
</div>

            {/* CONSENT */}
            <label className="job-application-modal__consent">
              <input type="checkbox" />

              <span>
                I agree to receive updates and marketing
                communications from <strong>Xyncely</strong>
              </span>
            </label>

            {error && (
              <p className="job-application-modal__error">
                {error}
              </p>
            )}

            {/* SUBMIT */}
            <button
              type="submit"
              className={`job-application-modal__submit ${
                isFormFilled
                  ? "job-application-modal__submit--active"
                  : ""
              }`}
              disabled={!isFormFilled || loading}
            >
              {loading ? "Submitting..." : "Submit"}

              {!loading && <span>→</span>}
            </button>

            <p className="job-application-modal__privacy">
              We respect your privacy. No spam, ever.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default JobApplicationModal;